import { create } from 'zustand';

/**
 * The dock's own state — open/closed, which thread is showing, the unsent draft.
 *
 * A store rather than React state in AppShell because three unrelated places drive it: the
 * topbar's ⌘K pill, the global keyboard shortcut, and the dock's own header buttons. Threading a
 * `toggle` callback from the shell down through every one of those is what this avoids.
 *
 * `open` and `conversationId` persist per PERSON (keyed on the person id, not just the origin):
 * a shared machine that signs one person out and another in must not reopen the previous
 * person's thread — the API would refuse it (own conversations only), but the id would still
 * leak into the next person's storage and produce a puzzling "not found" on their first send.
 * The draft is deliberately not persisted: a half-typed question about someone's salary should
 * not survive in localStorage after the tab closes.
 */
export interface CopilotState {
  open: boolean;
  conversationId: string | null;
  draft: string;
  /** Whose storage slot the state below was loaded from; null until `hydrate` runs. */
  personId: string | null;
  toggle: () => void;
  openDock: () => void;
  closeDock: () => void;
  setConversation: (conversationId: string | null) => void;
  setDraft: (draft: string) => void;
  /** Loads this person's persisted `open` + `conversationId`; a no-op if already hydrated for them. */
  hydrate: (personId: string) => void;
}

interface Persisted {
  open: boolean;
  conversationId: string | null;
}

const STORAGE_PREFIX = 'hrms_ai_copilot:';

function storageKey(personId: string): string {
  return `${STORAGE_PREFIX}${personId}`;
}

// localStorage can throw (Safari private mode, a full quota, a sandboxed iframe) and must never
// take the dock down with it — every access is wrapped, and a failure just means the defaults.
function readPersisted(personId: string): Persisted | null {
  try {
    const raw = window.localStorage.getItem(storageKey(personId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { open, conversationId } = parsed as Partial<Persisted>;
    return {
      open: open === true,
      conversationId: typeof conversationId === 'string' ? conversationId : null,
    };
  } catch {
    return null;
  }
}

function writePersisted(personId: string, value: Persisted): void {
  try {
    window.localStorage.setItem(storageKey(personId), JSON.stringify(value));
  } catch {
    // Memory-only for this session; a reload simply starts the dock closed.
  }
}

export const useCopilotStore = create<CopilotState>()((set, get) => {
  // Every state change that matters for persistence funnels through here, so the write happens
  // in exactly one place rather than in each action.
  const persist = (next: Partial<Persisted>) => {
    const state = get();
    const merged = { ...state, ...next };
    set(next);
    if (state.personId) {
      writePersisted(state.personId, {
        open: merged.open,
        conversationId: merged.conversationId,
      });
    }
  };

  return {
    open: false,
    conversationId: null,
    draft: '',
    personId: null,
    toggle: () => persist({ open: !get().open }),
    openDock: () => persist({ open: true }),
    closeDock: () => persist({ open: false }),
    setConversation: (conversationId) => persist({ conversationId }),
    setDraft: (draft) => set({ draft }),
    hydrate: (personId) => {
      if (get().personId === personId) return;
      const stored = readPersisted(personId);
      set({
        personId,
        open: stored?.open ?? false,
        conversationId: stored?.conversationId ?? null,
        draft: '',
      });
    },
  };
});
